/**
 * Success screen — shown after /coupons/claim returns 200 (or after an
 * idempotent replay). Confetti via CSS only — we deliberately avoid
 * adding canvas-confetti to keep the bundle clean.
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  Alert,
} from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { ClaimReceipt } from '../types';
import { brand } from '../../../config/brand';

interface ReceiptScreenProps {
  receipt: ClaimReceipt;
}

const fmtDate = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Pick a primary CTA based on the receipt content. The order matters:
 *  1. Any *_on_subscribe pending → push them to /pricing to unlock.
 *  2. credits_grant applied → push them to /dashboard to see their balance.
 *  3. free_months applied → push them to /dashboard to start building.
 *  4. Fallback → /dashboard.
 */
function pickPrimaryCta(receipt: ClaimReceipt) {
  if (receipt.pending.length > 0) {
    const targetPlan = receipt.pending[0].trigger?.plan;
    return {
      to: '/pricing',
      label: targetPlan
        ? `See ${targetPlan.charAt(0).toUpperCase() + targetPlan.slice(1)} plans`
        : 'See plans',
    };
  }
  return { to: '/dashboard', label: 'Go to dashboard' };
}

const ReceiptScreen: React.FC<ReceiptScreenProps> = ({ receipt }) => {
  // freeAccessUntil / freeAccessPlan get echoed back inside each
  // applied item that drove them — pick them off the first applied
  // free_months row we see (the backend only emits one even for combos).
  const freeAccess = receipt.applied.find(
    (a) => a.type === 'free_months' && a.freeAccessUntil,
  );
  const freeAccessUntilLabel = fmtDate(freeAccess?.freeAccessUntil);

  const cta = pickPrimaryCta(receipt);

  return (
    <Paper
      elevation={3}
      data-testid="receipt-screen"
      sx={{
        p: { xs: 3, sm: 4 },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Lightweight CSS confetti — purely decorative, no JS animation. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -8,
          left: 0,
          right: 0,
          height: 80,
          pointerEvents: 'none',
          background:
            'radial-gradient(circle at 10% 20%, ' +
            brand.colors.fire +
            ' 0 6px, transparent 7px), ' +
            'radial-gradient(circle at 30% 60%, ' +
            brand.colors.fireSecondary +
            ' 0 5px, transparent 6px), ' +
            'radial-gradient(circle at 55% 25%, ' +
            brand.colors.fireLight +
            ' 0 6px, transparent 7px), ' +
            'radial-gradient(circle at 75% 70%, ' +
            brand.colors.fireDark +
            ' 0 4px, transparent 5px), ' +
            'radial-gradient(circle at 90% 30%, ' +
            brand.colors.fire +
            ' 0 5px, transparent 6px)',
          animation: 'confettiFall 1.6s ease-out 1',
          '@keyframes confettiFall': {
            '0%': { opacity: 0, transform: 'translateY(-40px)' },
            '20%': { opacity: 1 },
            '100%': { opacity: 0, transform: 'translateY(60px)' },
          },
        }}
      />

      <Stack direction="row" spacing={2} alignItems="center">
        <CelebrationIcon color="primary" sx={{ fontSize: 48 }} />
        <Box>
          <Typography variant="overline" color="text.secondary">
            {receipt.idempotent ? 'Already redeemed' : 'Reward unlocked'}
          </Typography>
          <Typography variant="h4" component="h1" data-testid="receipt-tier">
            {receipt.tierName}
          </Typography>
        </Box>
      </Stack>

      {receipt.idempotent && (
        <Alert severity="info" data-testid="receipt-idempotent-notice">
          You've already redeemed this code — here's what you got.
        </Alert>
      )}

      {receipt.applied.length > 0 && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Applied to your account:
          </Typography>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            {receipt.applied.map((a, idx) => (
              <Typography
                key={idx}
                component="li"
                variant="body1"
                data-testid="receipt-applied-line"
              >
                {a.summary}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {freeAccessUntilLabel && (
        <Alert severity="success" data-testid="receipt-free-access-window">
          {freeAccess?.freeAccessPlan
            ? `${freeAccess.freeAccessPlan.charAt(0).toUpperCase() + freeAccess.freeAccessPlan.slice(1)} access ends ${freeAccessUntilLabel}.`
            : `Your free access ends ${freeAccessUntilLabel}.`}
        </Alert>
      )}

      {receipt.pending.length > 0 && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Pending — unlock by subscribing:
          </Typography>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            {receipt.pending.map((p) => (
              <Typography
                key={p.id}
                component="li"
                variant="body1"
                data-testid="receipt-pending-line"
              >
                {p.summary}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <Button
          component={RouterLink}
          to={cta.to}
          variant="contained"
          size="large"
          data-testid="receipt-cta"
          fullWidth
        >
          {cta.label}
        </Button>
      </Box>

      <Typography
        variant="body1"
        align="center"
        sx={{ mt: 1, fontStyle: 'italic' }}
        data-testid="receipt-welcome"
      >
        Welcome to {brand.fullProductName} 🎉
      </Typography>
    </Paper>
  );
};

export default ReceiptScreen;
